// Design tokens carried over from the Swift app's AppLayout / CGFloat defaults.
import { useColorScheme } from 'react-native';
import { telegram } from './platform/telegram';
import { useSettings } from './store/settings';

export const spacing = { xs: 4, s: 8, m: 12, l: 16, xl: 20, xxl: 24, xxxl: 32 } as const;
export const radius = { s: 8, m: 10, l: 12, pill: 999 } as const;
export const font = { caption: 12, footnote: 13, body: 16, headline: 17, title3: 20, title2: 22, title: 28, large: 34 } as const;

// Every theme is matte: muted, earthy tones on paper-like backgrounds, nothing glossy or neon.
type Colors = {
  tint: string; background: string; card: string; elevated: string; text: string; secondaryText: string; border: string;
  success: string; warning: string; danger: string; info: string; onTint: string; overlay: string; super: string;
  /** The streak flame when today is done. */
  streak: string;
};

const paperLight: Colors = {
  tint: '#B5573B', // matte terracotta
  background: '#F2ECE2', // beige white, with grain on top (Grain.tsx)
  card: '#E8E0D3',
  elevated: '#F7F2EA',
  text: '#2E2925',
  secondaryText: '#7A7068',
  border: '#DCD2C3',
  success: '#6F8F5E', // sage
  warning: '#C08A3E', // ochre
  danger: '#A8463A', // brick
  info: '#5F7A8C', // slate blue
  onTint: '#FBF7F1',
  overlay: 'rgba(46,41,37,0.35)',
  super: '#7D6A92', // dusty plum
  streak: '#C9743F',
};

const paperDark: Colors = {
  tint: '#C9765A',
  background: '#1F1B18',
  card: '#2A2521',
  elevated: '#332D28',
  text: '#EDE6DC',
  secondaryText: '#A89D92',
  border: '#3A332D',
  success: '#8AA67A',
  warning: '#D3A45A',
  danger: '#C2675A',
  info: '#7F98A8',
  onTint: '#FBF7F1',
  overlay: 'rgba(0,0,0,0.5)',
  super: '#9C8AB0',
  streak: '#D08A55',
};

export type ThemeId = 'modern' | 'mediterranean' | 'constructivist' | 'asian' | 'oriental' | 'developer';

/** What the web page draws over the app for a theme (ThemeLayer.web.tsx); native shows colours only. */
export type Decor = 'none' | 'constructivist' | 'motifs' | 'matrix';

type ThemeDef = {
  light: Colors; dark: Colors;
  /** Paper grain strength (0 = none). */
  grain: number;
  /** Web font family (src/assets/fonts/fonts.css). */
  font: string;
  decor: Decor;
  /** Always this scheme, whatever the system says (the Matrix is never light). */
  scheme?: 'light' | 'dark';
};

const matrix: Colors = {
  tint: '#45D46A', background: '#020804', card: '#06140A', elevated: '#0A1D0F', text: '#5BE07C', secondaryText: '#2F9A4C', border: '#0F3A1C',
  success: '#45D46A', warning: '#B5D84A', danger: '#D8604F', info: '#4FC2A0', onTint: '#020804', overlay: 'rgba(0,0,0,0.7)', super: '#8FE3A4', streak: '#45D46A',
};

export const THEMES_DEF: Record<ThemeId, ThemeDef> = {
  // Default: grainy beige paper with matte orange-red.
  modern: { light: paperLight, dark: paperDark, grain: 0.06, font: 'Chefi', decor: 'none' },
  // A Spanish casa / Italian house: whitewash, terracotta tiles, olive, a majolica blue. Elegant
  // Renaissance handwriting: chancery script headings, Garamond italic text.
  mediterranean: {
    light: { ...paperLight, tint: '#A9502E', background: '#F4EEE3', card: '#EADBC6', elevated: '#F9F4EC', text: '#33261E', secondaryText: '#7D6A5C', border: '#DECBB2', success: '#7A8450', warning: '#C8963E', info: '#3E6489', streak: '#C0703F' },
    dark: { ...paperDark, tint: '#C8714D', background: '#211813', card: '#2E211A', elevated: '#382920', border: '#433126', success: '#9AA36C', info: '#7194B8' },
    grain: 0.06, font: 'ChefiCasa', decor: 'none',
  },
  // Constructivism: cream, red, black and a little ochre; shapes on the page, poster headings.
  constructivist: {
    light: { ...paperLight, tint: '#B0362A', background: '#EFE6D2', card: '#E4D8BF', elevated: '#F6EFE0', text: '#1C1A17', secondaryText: '#5E564B', border: '#CFC1A3', success: '#4F6B4A', warning: '#C2A04A', danger: '#8E2A22', info: '#2F4A63', super: '#1C1A17', streak: '#B0362A' },
    dark: { ...paperDark, tint: '#D0574A', background: '#1A1816', card: '#262320', elevated: '#302C28', text: '#EFE6D2', border: '#3C3730', warning: '#D2B460', info: '#6F8DA8', super: '#EFE6D2' },
    grain: 0.07, font: 'ChefiConstruct', decor: 'constructivist',
  },
  // Ink and a vermilion seal on rice paper.
  asian: {
    light: { ...paperLight, tint: '#A94438', background: '#F1EFE9', card: '#E5E2DA', elevated: '#F8F6F1', text: '#262626', border: '#D7D3C9' },
    dark: { ...paperDark, tint: '#C66A5D', background: '#1A1A19', card: '#252523', elevated: '#2E2E2B', border: '#363633' },
    grain: 0.05, font: 'Chefi', decor: 'none',
  },
  // Warm Islamic greens (olive to jade) with gold on parchment; traditional geometric motifs
  // (khatam, shamseh, girih star) as corner ornaments joined by a frame, like a card.
  oriental: {
    light: { ...paperLight, tint: '#4A7A3D', background: '#F1EFE1', card: '#E3E3CB', elevated: '#F7F6EC', text: '#26301F', secondaryText: '#68705A', border: '#D1D2B4', success: '#6E8F45', warning: '#B8913F', danger: '#9E4A3C', info: '#3F7466', super: '#B8913F', streak: '#B8913F' },
    dark: { ...paperDark, tint: '#8AAA68', background: '#15190F', card: '#202717', elevated: '#29321F', text: '#ECEAD6', secondaryText: '#A5A88E', border: '#313B25', success: '#93B169', warning: '#CDA85A', info: '#6FA191', super: '#CDA85A', streak: '#CDA85A' },
    grain: 0.06, font: 'ChefiOriental', decor: 'motifs',
  },
  // The Matrix: green on black, terminal face, falling code. Always dark, no grain.
  developer: { light: matrix, dark: matrix, grain: 0, font: 'ChefiMatrix', decor: 'matrix', scheme: 'dark' },
};

export type Palette = Colors & { scheme: 'light' | 'dark'; grain: number; font: string; decor: Decor; theme: ThemeId };

const cache = new Map<string, Palette>();

/**
 * Current palette: the chosen theme, light or dark like the system (inside Telegram the Mini
 * App follows Telegram's light/dark setting).
 */
export function usePalette(): Palette {
  const system = useColorScheme();
  const chosen = useSettings(s => s.theme);
  const theme: ThemeId = chosen in THEMES_DEF ? chosen : 'modern';
  const def = THEMES_DEF[theme];
  const scheme = def.scheme ?? telegram.colorScheme ?? (system === 'dark' ? 'dark' : 'light');
  const key = `${theme}:${scheme}`;
  let palette = cache.get(key);
  if (!palette) {
    // Grain lightens dark paper (screen blend), so it is weaker there.
    palette = { ...def[scheme], scheme, grain: scheme === 'dark' ? def.grain * 0.6 : def.grain, font: def.font, decor: def.decor, theme };
    cache.set(key, palette);
  }
  return palette;
}

/** Matte placeholder tints for items without a photo. */
export const PHOTO_TINTS = {
  light: ['#E9D3C2', '#D6DCC8', '#D3DADD', '#DFD4DC', '#E8DCB9', '#E3CFC9'],
  dark: ['#4A3A30', '#3B4234', '#363F44', '#43383F', '#4A4230', '#47362F'],
  matrix: ['#06140A', '#081A0D', '#0A2010', '#071710'],
};

/** Matte chart colours (nutrition donut). */
export const CHART_COLORS = ['#C9A04F', '#6F8FA3', '#B5654F', '#7E9A6A'];

/** Search-result kind colours (Swift GlobalSearchKind.tint). */
export const kindColors = {
  recipe: '#B5573B',
  mealplan: '#7D6A92',
  cookbook: '#8A6544',
  ingredient: '#6F8F5E',
  utensil: '#4E8580',
  countryCookbook: '#5F7A8C',
} as const;
